import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { getCityBySlug, cities } from './cities';
import {
  MapPin,
  ArrowRight,
  Check,
  X,
  Clock,
  DollarSign,
  Zap,
  Shield,
  Ruler,
  FileText,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function LocationPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal } = useAuth();

  const loc = getCityBySlug(citySlug);
  if (!loc) return <Navigate to="/404" replace />;

  const { city, state, stateFullName } = loc;

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/measure');
    } else {
      openAuthModal('register');
    }
  };

  const comparisonData = [
    { feature: 'Cost', squarecount: 'Flat monthly — unlimited', drone: '$150-300/visit', eagleview: '$40-75/report', winner: 'sc' },
    { feature: 'Turnaround', squarecount: 'Instant (seconds)', drone: '1-3 days scheduling', eagleview: '24-48 hours', winner: 'sc' },
    { feature: 'Weather Dependent', squarecount: 'No', drone: 'Yes', eagleview: 'No', winner: 'sc' },
    { feature: 'Roof Area & Pitch', squarecount: true, drone: true, eagleview: true, winner: 'tie' },
    { feature: 'Material Estimates', squarecount: true, drone: 'Sometimes', eagleview: true, winner: 'tie' },
    { feature: 'PDF Reports', squarecount: true, drone: 'Varies', eagleview: true, winner: 'tie' },
    { feature: 'Gutter Measurements', squarecount: true, drone: 'Extra fee', eagleview: 'Add-on ($$$)', winner: 'sc' },
    { feature: 'No Site Visit Needed', squarecount: true, drone: false, eagleview: true, winner: 'sc' },
    { feature: 'White-Label Reports', squarecount: true, drone: 'No', eagleview: 'Enterprise only', winner: 'sc' },
    { feature: 'Cancel Anytime', squarecount: true, drone: 'N/A', eagleview: false, winner: 'sc' },
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Instant Results',
      desc: `Get roof measurements for any ${city} property in seconds. No waiting for drone pilots or scheduling site visits.`,
    },
    {
      icon: DollarSign,
      title: 'Save $500+/Month',
      desc: `${city} contractors using SquareCount save an average of $500+/month compared to per-report services like EagleView.`,
    },
    {
      icon: Ruler,
      title: 'Accurate Measurements',
      desc: `Satellite-based area, pitch, and ridge/valley calculations for ${city} roofs — residential and commercial.`,
    },
    {
      icon: FileText,
      title: 'Professional PDF Reports',
      desc: `Generate branded PDF reports instantly. Perfect for quoting ${city} homeowners and property managers on the spot.`,
    },
    {
      icon: Shield,
      title: 'No Contracts',
      desc: 'Month-to-month billing. Cancel anytime with one click. No long-term commitments or hidden fees.',
    },
    {
      icon: Clock,
      title: 'Quote Jobs On-Site',
      desc: `Pull up any ${city}, ${state} address while at the property. Measure, estimate, and close — all in one visit.`,
    },
  ];

  const nearbyLinks = cities
    .filter((c) => c.slug !== citySlug)
    .slice(0, 6);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `SquareCount Roof Measurement Software — ${city}, ${state}`,
    description: `Satellite roof measurement software for roofing contractors in ${city}, ${stateFullName}. Get instant roof area, pitch, and material estimates for any property.`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: `https://squarecount.io/locations/${citySlug}`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free tier with 3 measurements per month. The $79/mo Contractor plan includes unlimited measurements, editable takeoffs, and job costing — free 7-day trial.',
    },
    areaServed: {
      '@type': 'City',
      name: city,
      containedInPlace: {
        '@type': 'State',
        name: stateFullName,
      },
    },
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>{`Roof Measurement Software in ${city}, ${state} | SquareCount`}</title>
        <meta
          name="description"
          content={`Instant satellite roof measurements for roofing contractors in ${city}, ${stateFullName}. Get accurate roof area, pitch, and material estimates — no site visit required. Start free.`}
        />
        <meta property="og:title" content={`Roof Measurement Software in ${city}, ${state} | SquareCount`} />
        <meta
          property="og:description"
          content={`${city} roofing contractors use SquareCount for instant satellite roof measurements. One flat monthly plan, up to 90% cheaper than EagleView — free 7-day Contractor trial.`}
        />
        <link rel="canonical" href={`https://squarecount.io/locations/${citySlug}`} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23f97316\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />

        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-6">
              <MapPin className="w-4 h-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-medium">{city}, {stateFullName}</span>
            </div>

            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight mb-6">
              Satellite Roof Measurements in{' '}
              <span className="text-orange-500">{city}, {state}</span>
            </h1>

            <p className="text-xl text-slate-300 mb-8 max-w-2xl">
              Roofing contractors in {city} trust SquareCount for instant satellite
              measurements. Get accurate roof area, pitch, and material estimates
              for any property in {city} — no site visit required.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleGetStarted}
                className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-8 font-heading font-bold tracking-wide text-lg btn-tactile"
              >
                Start Measuring Roofs in {city}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/pricing')}
                className="h-14 px-8 font-heading font-bold tracking-wide text-lg border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 bg-transparent"
              >
                See Pricing
              </Button>
            </div>

            <p className="text-slate-500 text-sm mt-4">Free tier: 3 measurements/month. No credit card required.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              How It Works in {city}
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Measure any roof in {city}, {state} in three simple steps.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Enter the Address',
                desc: `Type any ${city}, ${state} address into SquareCount. Our satellite imagery covers all of ${city} and the surrounding ${stateFullName} metro area.`,
              },
              {
                step: '2',
                title: 'Get Instant Measurements',
                desc: `In seconds, get total roof area (in squares), pitch factor, ridges, valleys, and eaves for any ${city} property.`,
              },
              {
                step: '3',
                title: 'Download Your Report',
                desc: `Generate a professional PDF report with material estimates. Send it to your ${city} homeowner or keep it for your records.`,
              },
            ].map((item) => (
              <div key={item.step} className="relative p-6 bg-slate-900/50 border border-slate-800 rounded-lg">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mb-4 font-heading font-bold text-white text-lg">
                  {item.step}
                </div>
                <h3 className="font-heading font-bold text-lg text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              Why {city} Contractors Choose SquareCount
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-6 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors">
                  <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <h3 className="font-heading font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
              SquareCount vs Drone Pilots vs EagleView
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              See how SquareCount compares to hiring a local {city} drone pilot or ordering an EagleView report.
            </p>
          </div>

          <div className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-4 gap-4 p-4 bg-slate-800/80 border-b border-slate-700">
              <div className="text-slate-400 font-medium text-sm">Feature</div>
              <div className="text-center">
                <span className="text-orange-500 font-heading font-bold text-sm">SquareCount</span>
              </div>
              <div className="text-center">
                <span className="text-slate-400 font-heading font-bold text-sm">Local Drone</span>
              </div>
              <div className="text-center">
                <span className="text-slate-500 font-heading font-bold text-sm">EagleView</span>
              </div>
            </div>

            {comparisonData.map((row) => (
              <div
                key={row.feature}
                className={`grid grid-cols-4 gap-4 p-4 border-b border-slate-800/50 last:border-0 ${
                  row.winner === 'sc' ? 'bg-orange-500/5' : ''
                }`}
              >
                <div className="text-slate-300 text-sm">{row.feature}</div>
                {['squarecount', 'drone', 'eagleview'].map((col) => (
                  <div key={col} className="text-center">
                    {typeof row[col] === 'boolean' ? (
                      row[col] ? (
                        <Check className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-red-500 mx-auto" />
                      )
                    ) : (
                      <span
                        className={`text-sm ${
                          col === 'squarecount' && row.winner === 'sc'
                            ? 'text-orange-400 font-medium'
                            : 'text-slate-400'
                        }`}
                      >
                        {row[col]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-slate-800/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
            Start Measuring Roofs in {city} Today
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Join roofing contractors across {city}, {stateFullName} who use SquareCount
            for faster, cheaper, and more accurate roof measurements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleGetStarted}
              className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-10 font-heading font-bold tracking-wide text-lg btn-tactile"
            >
              Start Your 7-Day Contractor Trial
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/pricing')}
              className="h-14 px-10 font-heading font-bold tracking-wide text-lg border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 bg-transparent"
            >
              Compare Plans &amp; Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-12 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-6">
          <h3 className="font-heading font-bold text-xl text-white mb-6 text-center">
            Resources for {city} Roofing Contractors
          </h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/" className="p-4 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group text-center">
              <h4 className="font-heading font-bold text-white text-sm mb-1 group-hover:text-orange-400 transition-colors">Roof Measurement Software</h4>
              <p className="text-slate-500 text-xs">Unlimited satellite measurements, one flat price</p>
            </Link>
            <Link to="/free-alternative" className="p-4 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group text-center">
              <h4 className="font-heading font-bold text-white text-sm mb-1 group-hover:text-orange-400 transition-colors">Free Roof Measurements</h4>
              <p className="text-slate-500 text-xs">3 free reports/month, no credit card</p>
            </Link>
            <Link to="/pricing" className="p-4 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group text-center">
              <h4 className="font-heading font-bold text-white text-sm mb-1 group-hover:text-orange-400 transition-colors">View Pricing Plans</h4>
              <p className="text-slate-500 text-xs">Free, Pro, and Team plans</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Nearby Cities */}
      <section className="py-12 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-6">
          <h3 className="font-heading font-bold text-xl text-white mb-6 text-center">
            Also Serving Contractors In
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {nearbyLinks.map((c) => (
              <Link
                key={c.slug}
                to={`/locations/${c.slug}`}
                className="text-sm text-slate-400 hover:text-orange-400 bg-slate-900/50 border border-slate-800 hover:border-orange-500/30 rounded-full px-4 py-2 transition-colors"
              >
                {c.city}, {c.state}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
